import { Component, ElementRef, HostListener, Inject, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material';
import { MaintenanceTypeApi } from '@api/maintenanceType.api';
import { CustomMessagesService } from '@core/errors/custom-messages.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { MaintenanceType } from '@interfaces';
import { TranslateService } from '@ngx-translate/core';
import { sentry } from 'sentry';
import { Criteria, CriteriaGroup, CriteriaRules } from '../../../interfaces';
import { CompanyRankingService } from '../../../services/company-ranking.service';
import { DialogConfirmDeleteCriteriasGroupComponent } from '../dialog-confirm-delete-criterias-group/dialog-confirm-delete-criterias-group.component';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';

export interface RangeSlotsInUse {
  slots: number[];
  control: FormControl;
}

@Component({
  selector: 'dialog-edit-criterias-group',
  templateUrl: './dialog-edit-criterias-group.component.html',
  styleUrls: ['./dialog-edit-criterias-group.component.scss']
})
export class DialogEditCriteriasGroupComponent implements OnInit {

  public selectedDropdownIndex: number | null = null;
  public defaultPoints = 100;
  public defaultResultThreshold = 24;
  public isOnEditDialog = false;
  public isLoading = false;
  public deletionShouldBeEnabled = false;
  public criteriaRulePresence: { [key: string]: boolean } = {};
  public alreadySelectedRules: string[] = [];
  public criteriasGroup: CriteriaGroup;
  public criteriasGroupForm: FormGroup;
  public isPercentageGreaterBetter: string[] = ['accepted_percentage', 'completed_percentage', 'response_on_time_percentage', 'solution_on_time_percentage'];
  public isPercentageLowerBetter: string[] = ['refused_percentage', 'incompleted_percentage'];
  public isMeanTime: string[] = [
    'requested_interval',
    'on_negotiation_interval',
    'pending_interval',
    'scheduled_interval',
    'in_progress_interval',
    'reported_interval',
    'response_interval',
    'solution_interval',
    'average_response_time_in_days',
    'average_close_time_in_days'
  ];
  public typesByRange: string[] = ['response_on_time_percentage_by_range', 'solution_on_time_percentage_by_range'];
  public criteriaRules: CriteriaRules[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly matDialog: MatDialog,
    public readonly matDialogRef: MatDialogRef<DialogEditCriteriasGroupComponent>,
    private readonly translate: TranslateService,
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService,
    private readonly companyRanking: CompanyRankingService,
    private readonly customMessageService: CustomMessagesService,
    private maintenanceTypeApi: MaintenanceTypeApi
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  async ngOnInit(): Promise<void> {
    this.isLoading = true;
    this.setCriteriaRules();
    this.deletionShouldBeEnabled = this.data && this.data.deletionShouldBeEnabled;
    this.isOnEditDialog = !!(this.data && this.data.criteriaId);
    if (this.isOnEditDialog) {
      this.criteriasGroup = await this.companyRanking.getCriteriasGroupById(this.data.criteriaId);

      for (const criteria of this.criteriasGroup.criterias) {
        criteria.maintenanceTypes = await this.getMaintenanceTypes(criteria.value.maintenanceTypeIds);
      }
      this.criteriasGroupForm = new FormGroup({
        name: new FormControl(this.criteriasGroup.name || null, [Validators.required, Validators.maxLength(50)]),
        criterias: new FormArray(this.criteriasGroup.criterias.map((criteria) => this.createCriteriaFormGroup(criteria)) || null, [Validators.required])
      });

      this.updateAlreadySelectedRules();
      this.isLoading = false;
      return;
    }

    this.criteriasGroupForm = new FormGroup({
      name: new FormControl(null, [Validators.required, Validators.maxLength(50)]),
      criterias: new FormArray([], [Validators.required])
    });

    this.addCriteria();
    this.isLoading = false;
  }

  private async getMaintenanceTypes(typeIds: string[]): Promise<MaintenanceType[]> {
    const maintenanceTypes = [];

    if (!typeIds || typeIds.length === 0) {
      return maintenanceTypes;
    }

    for (const typeId of typeIds) {
      const maintenanceType = await this.maintenanceTypeApi.searchOne(typeId);
      maintenanceTypes.push(maintenanceType);
    }

    return maintenanceTypes;
  }

  public addCriteria(): void {
    const criteriasArray = this.criteriasGroupForm.get('criterias') as FormArray;

    const percentRangesFormArray = new FormArray([], { validators: [this.rangeValidator.bind(this)] });

    percentRangesFormArray.push(this.addPercentRangeFormGroup());

    const formGroup = new FormGroup({
      ruleName: new FormControl(null, [Validators.required]),
      maintenanceTypes: new FormControl(null),
      value: new FormGroup({})
    });

    formGroup.controls.ruleName.valueChanges.subscribe((value) => {
      if (this.typesByRange.includes(value)) {
        formGroup.setControl('value', new FormGroup({
          percentRange: percentRangesFormArray
        }));
      } else {
        formGroup.setControl('value', new FormGroup({
          points: new FormControl(null, [Validators.required, Validators.max(1000)]),
          resultThreshold: new FormControl(null, [Validators.max(1000)])
        }));
      }
      formGroup.markAsTouched();
      formGroup.updateValueAndValidity();
    });


    criteriasArray.push(formGroup);
  }

  public addRangeCriteria(): void {
    const criteriasArray = this.criteriasGroupForm.get('criterias') as FormArray;

    const percentRangesFormArray = new FormArray([], { validators: [this.rangeValidator.bind(this)] });

    percentRangesFormArray.push(this.addPercentRangeFormGroup());

    criteriasArray.push(new FormGroup({
      ruleName: new FormControl(null, [Validators.required]),
      maintenanceTypes: new FormControl(null),
      value: new FormGroup({
        points: new FormControl(null, [Validators.required, Validators.max(1000)]),
        resultThreshold: new FormControl(null, [Validators.max(1000)]),
        percentRange: percentRangesFormArray
      })
    }));
  }

  public rangeValidator(group: FormArray): ValidationErrors | null {
    const rangeControls = group.controls as FormControl[];
    for (const rangeControl of rangeControls) {
      rangeControl.setErrors(null);
    }

    const rangesSlotsInUse: RangeSlotsInUse[] = this.getRangeSlotsInUse(rangeControls);
    const rangesControlWithConflict: FormControl[] = this.getRangeControlWithConflicts(rangesSlotsInUse);

    for (const rangeWithConflict of rangesControlWithConflict) {
      rangeWithConflict.setErrors({ rangeConflict: true });
    }

    return null;
  }

  public hasError(control: AbstractControl): boolean {
    return control.status === 'INVALID' ? true : false;
  }

  public getRangeSlotsInUse(rangeControls: FormControl[]): RangeSlotsInUse[] {
    const rangesSlotsInUse = [];

    for (const rangeControl of rangeControls) {
      const range = rangeControl.value;

      const slots = [];
      const start = range.start < range.end ? range.start : range.end;
      const end = range.start > range.end ? range.start : range.end;

      for (let i = start; i <= end; i++) {
        slots.push(i);
      }

      rangesSlotsInUse.push({
        slots,
        control: rangeControl
      });
    }

    return rangesSlotsInUse;
  }

  public getRangeControlWithConflicts(rangesSlotsInUse: RangeSlotsInUse[]): FormControl[] {
    const rangesControlWithConflict: FormControl[] = [];

    for (const rangeSlots of rangesSlotsInUse) {
      const rangeSlotsAreInUseByAnotherControl = rangesSlotsInUse.some((otherRangeSlots) => {
        if (otherRangeSlots.control === rangeSlots.control) {
          return false;
        }

        return rangeSlots.slots.some((slot) => otherRangeSlots.slots.includes(slot));
      });

      if (!rangeSlotsAreInUseByAnotherControl) {
        continue;
      }

      rangesControlWithConflict.push(rangeSlots.control);
    }

    return rangesControlWithConflict;
  }

  public addPercentRange(percentRanges: FormArray): void {
    percentRanges.push(this.addPercentRangeFormGroup());
  }

  public addPercentRangeFormGroup(percentRange = null): FormGroup {
    return new FormGroup({
      start: new FormControl(percentRange != null ? percentRange.start : 0, [Validators.required, Validators.min(0), Validators.max(100)]),
      end: new FormControl(percentRange != null ? percentRange.end : 0, [Validators.required, Validators.min(0), Validators.max(100)]),
      discount: new FormControl(percentRange != null ? percentRange.discount : 0, [Validators.required, Validators.min(0), Validators.max(100)]),
      points: new FormControl(percentRange != null ? percentRange.points : 0, [Validators.required, Validators.min(0), Validators.max(1000)])
    });
  }

  public removeCriteria(criteriaToRemove: FormGroup): void {
    const criterias = this.criteriasGroupForm.get('criterias') as FormArray;
    let indexToRemove = -1;

    criterias.controls.forEach((criteria, index) => {
      if (criteria === criteriaToRemove) {
        indexToRemove = index;
      }
    });

    if (indexToRemove !== -1) {
      criterias.removeAt(indexToRemove);
    }
    this.updateAlreadySelectedRules();
  }

  public async applyCriterias(): Promise<void> {
    const criteriasGroupValue = this.toCriteriaGroupMapper(this.criteriasGroupForm.getRawValue());
    try {
      await this.companyRanking.updateCriteriasGroup(this.criteriasGroup.id, criteriasGroupValue);
      this.customMessageService.success('DIALOG_EDIT_CRITERIAS_GROUP.MESSAGES.SUCCESS_ON_UPDATE');
    }
    catch (err) {
      this.customMessageService.error('DIALOG_EDIT_CRITERIAS_GROUP.MESSAGES.ERROR_ON_UPDATE');
      sentry.captureException(err);
    }
    finally {
      this.matDialogRef.close(true);
    }
  }

  public async addCriteriasGroup(): Promise<void> {
    const criteriasGroupValue = this.toCriteriaGroupMapper(this.criteriasGroupForm.getRawValue());
    try {
      await this.companyRanking.addCriteriasGroup(criteriasGroupValue);
      this.customMessageService.success('DIALOG_EDIT_CRITERIAS_GROUP.MESSAGES.SUCCESS_ON_CREATE');
    }
    catch (err) {
      this.customMessageService.error('DIALOG_EDIT_CRITERIAS_GROUP.MESSAGES.ERROS_ON_CREATE');
      sentry.captureException(err);
    }
    finally {
      this.matDialogRef.close(true);
    }
  }

  public toCriteriaGroupMapper(value: any): any {
    return {
      name: value.name,
      criterias: value.criterias.map((criteria) => {
        const maintenanceTypeIds = criteria.maintenanceTypes && criteria.maintenanceTypes.map((maintenanceType) => maintenanceType.id) || [];
        delete criteria.maintenanceTypes;
        return {
          ruleName: criteria.ruleName,
          value: {
            ...criteria.value,
            maintenanceTypeIds: maintenanceTypeIds,
          }
        };
      })
    };
  }

  public deleteCriteriaGroup(): void {
    const dialogConfirmDeleteCriteriasGroup = this.matDialog.open(DialogConfirmDeleteCriteriasGroupComponent, {
      panelClass: ['dialog-confirm-delete-criterias-group'],
      width: '462px',
      data: {
        id: this.criteriasGroup.id
      }
    });

    dialogConfirmDeleteCriteriasGroup.afterClosed().subscribe((result) => {
      if (result) {
        this.matDialogRef.close(true);
      }
    });
  }

  public deleteRangeByIndex(percentRanges: FormArray, index: number): void {
    const percentRangesFormArray = percentRanges as FormArray;
    if (percentRangesFormArray.length > 1) {
      percentRangesFormArray.removeAt(index);
    }
  }

  public handleRuleChange(): void {
    this.updateAlreadySelectedRules();
  }

  public updateAlreadySelectedRules(): void {
    this.alreadySelectedRules = this.criteriasGroupForm.getRawValue().criterias.map((criteria) => criteria.ruleName);
  }

  private createCriteriaFormGroup(criteria: Criteria): FormGroup {
    const formGroup = new FormGroup({
      ruleName: new FormControl(criteria.ruleName, [Validators.required]),
      value: new FormGroup({}),
      maintenanceTypes: new FormControl(criteria.maintenanceTypes.map((maintenanceType) => {
        return {
          id: maintenanceType.id,
          name: maintenanceType.name
        };
      }))
    });

    if (!this.typesByRange.includes(criteria.ruleName)) {
      formGroup.setControl('value', new FormGroup({
        points: new FormControl(criteria.value.points, [Validators.required, Validators.max(1000)]),
        resultThreshold: new FormControl(criteria.value.resultThreshold, [Validators.max(1000)]),
      }));

      return formGroup;
    }

    formGroup.setControl('value', new FormGroup({
      percentRange: new FormArray(
        criteria.value.percentRange.map((percentRange) => this.addPercentRangeFormGroup(percentRange)) || [],
        { validators: [this.rangeValidator.bind(this)] }
      )
    }));

    return formGroup;
  }

  private setCriteriaRules(): void {
    this.criteriaRules = [
      { text: this.translate.instant('DIALOG_EDIT_CRITERIAS_GROUP.CRITERIAS.ACCEPTED_PERCENTAGE'), value: 'accepted_percentage' },
      { text: this.translate.instant('DIALOG_EDIT_CRITERIAS_GROUP.CRITERIAS.REFUSED_PERCENTAGE'), value: 'refused_percentage' },
      { text: this.translate.instant('DIALOG_EDIT_CRITERIAS_GROUP.CRITERIAS.COMPLETED_PERCENTAGE'), value: 'completed_percentage' },
      { text: this.translate.instant('DIALOG_EDIT_CRITERIAS_GROUP.CRITERIAS.INCOMPLETED_PERCENTAGE'), value: 'incompleted_percentage' },
      { text: this.translate.instant('DIALOG_EDIT_CRITERIAS_GROUP.CRITERIAS.RESPONSE_ON_TIME_PERCENTAGE'), value: 'response_on_time_percentage' },
      { text: this.translate.instant('DIALOG_EDIT_CRITERIAS_GROUP.CRITERIAS.SOLUTION_ON_TIME_PERCENTAGE'), value: 'solution_on_time_percentage' },
      { text: this.translate.instant('DIALOG_EDIT_CRITERIAS_GROUP.CRITERIAS.RESPONSE_ON_TIME_PERCENTAGE_BY_RANGE'), value: 'response_on_time_percentage_by_range' },
      { text: this.translate.instant('DIALOG_EDIT_CRITERIAS_GROUP.CRITERIAS.SOLUTION_ON_TIME_PERCENTAGE_BY_RANGE'), value: 'solution_on_time_percentage_by_range' },
      { text: this.translate.instant('DIALOG_EDIT_CRITERIAS_GROUP.CRITERIAS.AVERAGE_RATING'), value: 'average_rating' },
      { text: this.translate.instant('DIALOG_EDIT_CRITERIAS_GROUP.CRITERIAS.REQUESTED_INTERVAL'), value: 'requested_interval' },
      { text: this.translate.instant('DIALOG_EDIT_CRITERIAS_GROUP.CRITERIAS.ON_NEGOTIATION_INTERVAL'), value: 'on_negotiation_interval' },
      { text: this.translate.instant('DIALOG_EDIT_CRITERIAS_GROUP.CRITERIAS.PENDING_INTERVAL'), value: 'pending_interval' },
      { text: this.translate.instant('DIALOG_EDIT_CRITERIAS_GROUP.CRITERIAS.SCHEDULED_INTERVAL'), value: 'scheduled_interval' },
      { text: this.translate.instant('DIALOG_EDIT_CRITERIAS_GROUP.CRITERIAS.IN_PROGRESS_INTERVAL'), value: 'in_progress_interval' },
      { text: this.translate.instant('DIALOG_EDIT_CRITERIAS_GROUP.CRITERIAS.REPORTED_INTERVAL'), value: 'reported_interval' },
      { text: this.translate.instant('DIALOG_EDIT_CRITERIAS_GROUP.CRITERIAS.RESPONSE_INTERVAL'), value: 'response_interval' },
      { text: this.translate.instant('DIALOG_EDIT_CRITERIAS_GROUP.CRITERIAS.SOLUTION_INTERVAL'), value: 'solution_interval' },
      { text: this.translate.instant('DIALOG_EDIT_CRITERIAS_GROUP.CRITERIAS.AVERAGE_RESPONSE_TIME_IN_DAYS'), value: 'average_response_time_in_days' },
      { text: this.translate.instant('DIALOG_EDIT_CRITERIAS_GROUP.CRITERIAS.AVERAGE_CLOSE_TIME_IN_DAYS'), value: 'average_close_time_in_days' },
    ];
  }
}
